import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { createFilterQuery } from "@via/pages/data/data-collections/utils/helper.ts";
import { ROUTES } from "@via/utils/constants.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import { DatasetListResponseItem, PaginatedResponse } from "common";
import { useNavigate } from "react-router-dom";

export const fetchCollectionList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: Array<{ id: string; value: unknown }>,
  ownerId?: number
): Promise<PaginatedResponse<DatasetListResponseItem>> => {
  try {
    const baseUrl = "/api/collection/v1";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    if (sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }

    if (filter && filter.length > 0) {
      const queryFilter = createFilterQuery(filter);
      queryParams.append("filter", queryFilter);
    }
    if (ownerId) {
      queryParams.append("ownerId", ownerId.toString());
    }

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useCollectionList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true,
  ownerId?: number
): UseQueryResult<PaginatedResponse<DatasetListResponseItem>, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<PaginatedResponse<DatasetListResponseItem>, Error>({
    queryKey: ["/api/collection/v1", take, skip, sort, filter, ownerId],
    queryFn: async () =>
      await fetchCollectionList(take, skip, sort, filter, ownerId),
    retry: false,
    enabled,
  });
};

export const useCreateCollection = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (data: { name: string; projectId: number }) => {
      const res = await axios.post("/api/collection/v1", data);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "collection-successfully-created",
        message: "Dataset Successfully Created",
        details: "Dataset has been created",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey.some(
            key => typeof key === "string" && key.includes("/api/collection/v1")
          ),
        refetchType: "all",
      });
    },
    onError: error => {
      setNotification({
        id: "collection-creation-failed",
        message: "Dataset Create Failed",
        details: "Dataset could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteCollection = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: { id: string }) => {
      const res = await axios.delete(`/api/collection/v1/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "collection-successfully-deleted",
        message: "Dataset Successfully Deleted",
        details: "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the Collection list
      queryClient.invalidateQueries({ queryKey: ["/api/collection/v1"] });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "collection-deletion-failed",
        message: "Dataset Deletion Failed",
        details:
          error?.response?.data || "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateCollection = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (data: {
      collectionId: string;
      name: Record<number, string>;
    }) => {
      const res = await axios.patch(`/api/collection/v1`, data);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "collection-successfully-updated",
        message: "Dataset Successfully Updated",
        details: "Dataset has been updated",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the Collection list
      queryClient.invalidateQueries({ queryKey: ["/api/collection/v1"] });
    },
    onError: error => {
      setNotification({
        id: "collection-update-failed",
        message: "Dataset Update Failed",
        details: "Dataset could not be updated",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useSaveFilesToCollection = (): any => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: any) => {
      const res = await axios.post("/api/file/v1", payload);

      return res.data;
    },
    onSuccess: async () => {
      setNotification({
        id: "files-successfully-added-to-collection",
        message: "Files Successfully Added to Dataset",
        details: "Files have been added to dataset",
        variant: NOTIFICATION.SUCCESS,
      });

      queryClient.invalidateQueries({
        queryKey: ["api/collection/v1"],
      });

      queryClient.invalidateQueries({
        predicate: query => {
          return /\/api\/collection\/v1(\/\d+\/files)*/.test(
            query.queryKey[0] as string
          );
        },
      });

      await Promise.all([
        queryClient.refetchQueries({
          queryKey: ["api/collection/v1"],
        }),
        queryClient.refetchQueries({
          predicate: query =>
            /\/api\/collection\/v1(\/\d+\/files)*/.test(
              query.queryKey[0] as string
            ),
        }),
      ]);

      navigate(ROUTES.DATA);
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message;
      setNotification({
        id: "failed-to-save-files-to-collection",
        message: "Failed to Save Files to Dataset",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchDatasetNameExist = async (
  filter: {
    id: string;
    value: string;
  },
  ownerId?: number
): Promise<PaginatedResponse<DatasetListResponseItem>> => {
  try {
    const baseUrl = "/api/collection/v1";
    const queryParams = new URLSearchParams();

    queryParams.append("filter", `name:eq=${filter.value}`);

    if (ownerId) {
      queryParams.append("ownerId", ownerId.toString());
    }

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
